import {
  Card as MuiCard,
  CardHeader,
  CardContent,
  CardActions,
  CardMedia,
  Box,
  useTheme,
} from '@mui/material';
import { CardProps, CardHeaderConfig } from './Card.props';
import {
  cardLeftLayoutStyles,
  cardLeftIconContainerStyles,
  cardLeftHeaderStyles,
  cardLeftRightSectionStyles,
} from './Card.theme';
import { ReactNode } from 'react';

// Type guard to check if header is a CardHeaderConfig
const isCardHeaderConfig = (
  header: ReactNode | CardHeaderConfig
): header is CardHeaderConfig => {
  return (
    typeof header === 'object' &&
    header !== null &&
    !('$$typeof' in header) &&
    ('title' in header ||
      'subheader' in header ||
      'avatar' in header ||
      'action' in header)
  );
};

export const Card = ({
  header,
  media,
  content,
  actions,
  children,
  cardIcon,
  ...props
}: CardProps) => {
  const theme = useTheme();
  // If cardIcon position is "left", render split layout
  if (cardIcon?.position === 'left') {
    return (
      <MuiCard
        {...props}
        sx={[
          cardLeftLayoutStyles,

          ...(props.sx
            ? Array.isArray(props.sx)
              ? props.sx
              : [props.sx]
            : []),
        ]}
      >
        {/* Left section with icon */}
        <Box sx={cardLeftIconContainerStyles}>{cardIcon.icon}</Box>

        {/* Right section with header, content, actions */}
        <Box sx={cardLeftRightSectionStyles}>
          {header && (
            <>
              {isCardHeaderConfig(header) ? (
                <CardHeader
                  sx={{
                    ...cardLeftHeaderStyles,
                  }}
                  avatar={header.avatar}
                  title={header.title}
                  subheader={header.subheader}
                  action={header.action}
                />
              ) : (
                <CardHeader sx={cardLeftHeaderStyles} title={header} />
              )}
            </>
          )}
          {media && (
            <CardMedia
              sx={{ padding: 0, ...theme.typography.M3.body.small }}
              component="img"
              image={media.image}
              alt={media.alt || ''}
              height={media.height || 140}
            />
          )}
          {content && (
            <CardContent sx={{ padding: 0, ...theme.typography.M3.body.small }}>
              {content}
            </CardContent>
          )}
          {children && !content && (
            <CardContent sx={{ padding: 0, ...theme.typography.M3.body.small }}>
              {children}
            </CardContent>
          )}
          {actions && (
            <CardActions sx={{ padding: 0, ...theme.typography.M3.body.small }}>
              {actions}
            </CardActions>
          )}
        </Box>
      </MuiCard>
    );
  }

  // Default layout with cardIcon position "top" (or no cardIcon)
  const effectiveAvatar =
    cardIcon?.position === 'top' && !isCardHeaderConfig(header)
      ? cardIcon.icon
      : isCardHeaderConfig(header)
        ? header.avatar
        : undefined;

  return (
    <MuiCard {...props}>
      {header && (
        <>
          {isCardHeaderConfig(header) ? (
            <CardHeader
              avatar={
                cardIcon?.position === 'top' && !header.avatar
                  ? cardIcon.icon
                  : header.avatar
              }
              title={header.title}
              subheader={header.subheader}
              action={header.action}
            />
          ) : (
            <CardHeader title={header} avatar={effectiveAvatar} />
          )}
        </>
      )}
      {media && (
        <CardMedia
          component="img"
          image={media.image}
          alt={media.alt || ''}
          height={media.height || 140}
        />
      )}
      {content && <CardContent>{content}</CardContent>}
      {children && !content && <CardContent>{children}</CardContent>}
      {actions && <CardActions>{actions}</CardActions>}
    </MuiCard>
  );
};

export default Card;
